import type { FC } from 'react';
import Button from '@/components/UI/Button';
import { Animal, AnimalApiId } from '@/model/Animal.model';
//@ts-expect-error - old hook form - replace with `react-hook-form@latest`
import { FormProvider, useForm, useFormContext, Controller, ControllerRenderProps } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { checkStudyForDuplicates } from '../AssignIdentifiers/AssignIdentifiers.utils';
import { StudyApiId } from '@/model/Study.model';
import { Radio } from '@/components/UI/FormFields';
import { useSelector } from 'react-redux';
import { State } from '@/model/State.model';
import { useRequest } from '@/support/Hooks/request';
import { ISODate } from '@/model/Common.model';
import { _isEmpty, _isNil, _isNotEmpty } from '@/littledash';
import { startCase } from 'lodash';
import { validAnimalNameLength } from '@/components/Colony/Animals/Animals.utils.tsx';
import { isDateGreaterThanToday } from '@/helpers';
import DatePickerNative from '@/components/UI/DatePickerNative';
import { FormField } from '@/components/UI/FormFields';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';

interface EditSubjectProps {
  animal: Animal;
  studyApiId: StudyApiId;
  handleCallback: (message: string) => void;
  closeModal: () => void;
}

interface AnimalUploadFormData {
  name: string;
  dob: ISODate;
  sex: 'm' | 'f';
  tail: string;
  tag: string;
  donor: string;
  ear: string;
}

interface AnimalUpdatePayload {
  name: string;
  dob: ISODate | null;
  sex: 'm' | 'f';
  alt_ids: {
    tail: string;
    tag: string;
    donor: string;
    ear: string;
  };
}

const idFields = [
  {
    label: 'Name',
    name: 'name',
    required: true,
  },
  {
    label: 'Tail',
    name: 'tail',
  },
  {
    label: 'Ear',
    name: 'ear',
  },
  {
    label: 'Tag',
    name: 'tag',
  },
  {
    label: 'Donor',
    name: 'donor',
  },
];

const EditSubject: FC<EditSubjectProps> = ({ animal, studyApiId, handleCallback, closeModal }) => {
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const isDobRequired = entityFieldRequiredMap?.dob ?? true;

  const formMethods = useForm({
    defaultValues: {
      name: animal.name,
      dob: animal.dob || '',
      sex: animal.sex,
      ...animal.alt_ids,
    },
  });
  const { handleSubmit, control } = formMethods;
  const { sendRequest: updateAnimal, requestSending: saving } = useRequest({
    params: { id: animal.id },
    route: 'animal',
    method: 'patch',
  });

  const handlePayload = (data: AnimalUploadFormData): AnimalUpdatePayload => {
    return {
      name: data.name,
      dob: _isEmpty(data.dob) ? null : data.dob,
      sex: data.sex,
      alt_ids: {
        tail: data.tail,
        tag: data.tag,
        donor: data.donor,
        ear: data.ear,
      },
    };
  };

  const onSubmit = async (data: AnimalUploadFormData) => {
    await updateAnimal(handlePayload(data));
    handleCallback('Successfully updated animal');
    closeModal();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`center mv3 pa4 bg-white br2 shadow-4`}
      style={{ maxWidth: 420 }}
    >
      <div className="tc pb3">
        <h3 className="normal f4 lh-title">Edit {animal.name}</h3>
      </div>
      <FormProvider {...formMethods}>
        {idFields.map(({ name, label, required }) => (
          <EditSubjectNameField
            key={`${name}-field`}
            name={name}
            label={label}
            studyApiId={studyApiId}
            animalId={animal?.api_id}
            required={required}
          />
        ))}
        <div className="mb3">
          <FormField name="dob" label="Date of birth" required={isDobRequired}>
            <Controller
              name="dob"
              control={control}
              defaultValue={animal.dob || ''}
              rules={{
                validate: (value: string) => {
                  if (_isEmpty(value)) {
                    return !isDobRequired || 'DOB is required';
                  }
                  const selectedDate = new Date(value);
                  return !isDateGreaterThanToday(selectedDate) || 'DOB cannot be in the future';
                },
              }}
              render={({ onChange, value }: ControllerRenderProps) => (
                <DatePickerNative value={value || ''} onChange={onChange} testId="edit-subject-dob-input" />
              )}
            />
          </FormField>
        </div>
        <div className="mb3">
          <Radio
            name="sex"
            label="Sex"
            required
            options={[
              {
                label: 'Male',
                value: 'm',
              },
              {
                label: 'Female',
                value: 'f',
              },
            ]}
          />
        </div>
      </FormProvider>
      <Button submit className="mr3" disabled={saving} testId="edit-subject__save">
        Save
      </Button>
      <Button plain onClick={closeModal}>
        Cancel
      </Button>
    </form>
  );
};

interface EditSubjectNameFieldProps {
  name: string;
  label: string;
  studyApiId: StudyApiId;
  animalId?: AnimalApiId;
  required?: boolean;
}

const EditSubjectNameField: FC<EditSubjectNameFieldProps> = ({
  name,
  label,
  studyApiId,
  animalId,
  required = false,
}) => {
  const { register, errors } = useFormContext();
  const state = useSelector((state: State) => state?.team);
  const rules: { validate?: (value: string) => Promise<boolean | string> } = {};

  if (_isNil(animalId) || _isNil(studyApiId)) {
    return null;
  }

  if (name === 'tag' && state?.features?.assign_identifiers_tag_unique === true) {
    rules.validate = async (value) => {
      try {
        const result = await checkStudyForDuplicates(value, 'tag', studyApiId, animalId);
        return result ? true : 'This identifier is already in use';
      } catch (error) {
        return 'An error occurred';
      }
    };
  } else {
    rules.validate = async (value: string) => {
      if (required && _isEmpty(value)) {
        return `${startCase(name)} cannot be empty`;
      }
      if (name === 'name' && !validAnimalNameLength(value)) {
        return 'Name cannot be longer than 40 characters.';
      }
      return true;
    };
  }

  return (
    <div className="mb3">
      <label htmlFor={name}>{`${label}${required ? ' (required)' : ''}`}</label>
      <input
        data-testid={`edit-subject-${name}-input`}
        type="text"
        className={`${errors[name] ? 'input__error' : ''}`}
        name={name}
        ref={register(rules)}
      />
      <ErrorMessage errors={errors} name={name} render={({ message }) => <p className="f6 red lh-copy">{message}</p>} />
    </div>
  );
};

export default EditSubject;
